// Rung 3 — it repeats.
//
// A route is a different object from a round, and the difference is the
// whole of §21. A round's key is its identity *and* its publish
// authority at once, which is elegant for a delivery that dies at the
// door and wrong for a bus: handing tomorrow's driver the authority means
// handing over the key, and rotating the key rotates the link — so every
// parent's subscription would break every time the depot changes who is
// driving.
//
// So identity and authority come apart. The **root** stays here, at the
// depot, for the whole term, and is what the parents' link is made of.
// Each service day gets a **day key** derived from it and a certificate
// that vouches for that key for at most 48 hours. A driver's phone holds
// one day of authority and it expires on its own. Nobody's link moves.
//
// The day reaches that phone as an ordinary sealed ticket carrying the
// certificate inside it (§21.11) — the same artifact, the same QR and the
// same file as any other job. It is what makes the paragraph above
// something an operator can act on rather than a property of two loose
// values somebody has to keep together.

import { useCallback, useEffect, useMemo, useState } from "react";
import { THREAD_MODE, granularityDefault, profileFor } from "@wayfind/core";
import { Copyable, Field, Note, Panel, Qr, downloadFile } from "./Bits";
import { planInputFor, planKeyFor, type Dispatcher, type ReadTicket } from "../ops/dispatch";
import {
  localId,
  type FleetState,
  type StoredJob,
  type StoredRoute,
  type StoredRouteDay,
  type StoredStop
} from "../ops/state";
import { dateWords, plural, type Vocabulary } from "../ops/words";
import { config, nowSeconds, today } from "../config";

const DAY = 86400;

/** Rounds that have gone out on more than one day and are not already a route. */
export function repeatedRounds(state: FleetState): { planKey: string; job: StoredJob; days: number }[] {
  const already = new Set(state.routes.map(route => route.planKey));
  const byPlan = new Map<string, { job: StoredJob; days: Set<number> }>();
  for (const job of state.jobs) {
    if (job.routeId || already.has(job.planKey)) continue;
    const entry = byPlan.get(job.planKey) ?? { job, days: new Set<number>() };
    entry.days.add(Math.floor(job.createdUnixSeconds / DAY));
    if (job.createdUnixSeconds > entry.job.createdUnixSeconds) entry.job = job;
    byPlan.set(job.planKey, entry);
  }
  return [...byPlan.entries()]
    .filter(([, entry]) => entry.days.size >= 2)
    .map(([planKey, entry]) => ({ planKey, job: entry.job, days: entry.days.size }))
    .sort((a, b) => b.days - a.days);
}

export function Routes({
  state,
  vocabulary,
  dispatcher,
  onSave,
  onDispatchDay
}: {
  state: FleetState;
  vocabulary: Vocabulary;
  dispatcher: Dispatcher | null;
  onSave(route: StoredRoute): void;
  onDispatchDay(route: StoredRoute, day: StoredRouteDay, job: StoredJob): void;
}): JSX.Element {
  const [making, setMaking] = useState<{ stops: StoredStop[]; name: string } | null>(null);
  const candidates = useMemo(() => repeatedRounds(state), [state]);
  const profile = state.profile ?? "courier";

  return (
    <div className="stack">
      {state.routes.map(route => (
        <RouteCard
          key={route.id}
          route={route}
          state={state}
          vocabulary={vocabulary}
          dispatcher={dispatcher}
          onSave={onSave}
          onDispatchDay={onDispatchDay}
        />
      ))}

      {making ? (
        <NewRoute
          state={state}
          vocabulary={vocabulary}
          dispatcher={dispatcher}
          stops={making.stops}
          initialName={making.name}
          onCancel={() => setMaking(null)}
          onSave={route => { onSave(route); setMaking(null); }}
        />
      ) : (
        <Panel eyebrow={`Make a ${vocabulary.route}`}>
          {candidates.length ? (
            <div className="rows">
              {candidates.map(candidate => (
                <div key={candidate.planKey} className="rowcard">
                  <div className="rowcard__main">
                    <span className="rowcard__name">
                      {plural(candidate.job.stops.length, vocabulary.stop)} from{" "}
                      {candidate.job.stops[0]!.label}
                    </span>
                    <span className="rowcard__sub">Sent on {candidate.days} separate days</span>
                  </div>
                  <button
                    type="button"
                    className="button button--small"
                    onClick={() => setMaking({
                      stops: candidate.job.stops,
                      name: `${candidate.job.stops[0]!.label}`
                    })}
                  >
                    Make it a {vocabulary.route}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="small muted">
              {profileFor(profile).defaults.grantsRung === 3
                ? `Add the ${vocabulary.stop}s for a ${vocabulary.route} and set the term. `
                  + "Parents subscribe once and keep the same link until it ends."
                : `Nothing has gone out on the same places more than once yet.`}
            </p>
          )}
          {profileFor(profile).defaults.grantsRung === 3 ? (
            <div className="row row--end" style={{ marginTop: "0.6rem" }}>
              <button
                type="button"
                className="button"
                onClick={() => setMaking({ stops: [], name: "" })}
              >
                Start one from scratch
              </button>
            </div>
          ) : null}
        </Panel>
      )}
    </div>
  );
}

function NewRoute({
  state,
  vocabulary,
  dispatcher,
  stops,
  initialName,
  onCancel,
  onSave
}: {
  state: FleetState;
  vocabulary: Vocabulary;
  dispatcher: Dispatcher | null;
  stops: StoredStop[];
  initialName: string;
  onCancel(): void;
  onSave(route: StoredRoute): void;
}): JSX.Element {
  const profile = state.profile ?? "courier";
  const granularity = granularityDefault(profile);
  const [name, setName] = useState(initialName);
  const [from, setFrom] = useState(isoDate(nowSeconds()));
  const [to, setTo] = useState(isoDate(nowSeconds() + 120 * DAY));
  const [mode] = useState<number>(granularity.mode);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const create = useCallback(async () => {
    if (!dispatcher || !stops.length) return;
    setBusy(true);
    setProblem(null);
    try {
      const plan = planInputFor(profile, stops, { travelMode: 1 });
      const planBytes = dispatcher.planBytes(plan);
      const root = await dispatcher.newRouteRoot();
      const termTo = Math.floor(new Date(`${to}T23:59:00`).getTime() / 1000);
      const termFrom = Math.floor(new Date(`${from}T00:00:00`).getTime() / 1000);
      onSave({
        id: localId("route"),
        name: name.trim() || `${vocabulary.Route} ${state.routes.length + 1}`,
        createdUnixSeconds: nowSeconds(),
        rootSeedHex: root.seedHex,
        rootPublicKeyHex: root.publicKeyHex,
        planKey: planKeyFor(stops),
        planRefHex: dispatcher.planRefHex(planBytes),
        planB64: dispatcher.threads.bytesToBase64Url(planBytes),
        stops,
        mode,
        travelMode: 1,
        termFromUnixSeconds: termFrom,
        termToUnixSeconds: termTo,
        parentUrl: await dispatcher.termLink(root.seedHex, termTo),
        days: []
      });
    } catch (error) {
      setProblem(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }, [dispatcher, stops, profile, name, from, to, mode, state.routes.length, vocabulary, onSave]);

  return (
    <Panel
      eyebrow={`A new ${vocabulary.route}`}
      aside={<button type="button" className="button button--small" onClick={onCancel}>Cancel</button>}
    >
      <div className="stack stack--tight">
        <Field label="What it is called">
          <input className="input" value={name} onChange={event => setName(event.target.value)} placeholder="8A — morning" />
        </Field>
        <div className="grid2">
          <Field label="Term starts">
            <input className="input" type="date" value={from} onChange={event => setFrom(event.target.value)} />
          </Field>
          <Field label="Term ends" hint="The parents’ link lasts exactly this long.">
            <input className="input" type="date" value={to} onChange={event => setTo(event.target.value)} />
          </Field>
        </div>

        {stops.length ? (
          <div className="stops">
            {stops.map((stop, index) => (
              <div key={stop.id} className="stoprow stoprow--sunk">
                <span className="stoprow__index">{index + 1}</span>
                <div><p className="stoprow__label">{stop.label}</p></div>
                <span />
              </div>
            ))}
          </div>
        ) : (
          <Note title={`Add the ${vocabulary.stop}s on the composer first.`} tone="quiet">
            Send it once as a one-off, then come back and make it a {vocabulary.route}. The
            {" "}{vocabulary.stop}s carry over.
          </Note>
        )}

        <Note
          title={mode === THREAD_MODE.COARSE
            ? "Parents see the arrival, not the vehicle."
            : "This publishes a live position."}
          tone={mode === THREAD_MODE.COARSE ? "quiet" : "halt"}
        >
          {granularity.rationale}
        </Note>

        {problem ? (
          <div className="banner banner--halt" role="alert">
            <span className="banner__mark">No</span>
            <p className="banner__text">{problem}</p>
          </div>
        ) : null}

        <div className="row row--end">
          <button
            type="button"
            className="button button--primary"
            disabled={!dispatcher || !stops.length || busy}
            onClick={() => { void create(); }}
          >
            {busy ? "Minting…" : `Make the ${vocabulary.route}`}
          </button>
        </div>
      </div>
    </Panel>
  );
}

function RouteCard({
  route,
  state,
  vocabulary,
  dispatcher,
  onSave,
  onDispatchDay
}: {
  route: StoredRoute;
  state: FleetState;
  vocabulary: Vocabulary;
  dispatcher: Dispatcher | null;
  onSave(route: StoredRoute): void;
  onDispatchDay(route: StoredRoute, day: StoredRouteDay, job: StoredJob): void;
}): JSX.Element {
  const [deviceId, setDeviceId] = useState<string>("");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const devices = state.devices.filter(device => device.retiredUnixSeconds == null);
  const civil = today();
  const serviceDay = civil.year * 10000 + civil.month * 100 + civil.day;
  const already = route.days.find(day => day.serviceDay === serviceDay) ?? null;

  const dispatchToday = useCallback(async () => {
    if (!dispatcher) return;
    setBusy(true);
    setProblem(null);
    try {
      const midnight = new Date();
      midnight.setFullYear(civil.year, civil.month - 1, civil.day);
      midnight.setHours(0, 0, 0, 0);
      const notBefore = Math.floor(midnight.getTime() / 1000) - 2 * 3600;
      // 28 hours: a run that departs today and lands after midnight is
      // still today's service day (§21.9), and a depot minting this
      // afternoon wants it live before the phone leaves the charger. The
      // subscriber refuses anything over 48.
      const notAfter = notBefore + 28 * 3600;

      const device = devices.find(entry => entry.id === deviceId) ?? null;
      const issued = await dispatcher.issueRouteDay({
        // The root signs, because on a route-day ticket only the root
        // can: it is the seed the certificate and the day key both come
        // out of. The dispatcher's own issuer identity has no part here.
        rootSeedHex: route.rootSeedHex,
        // The term's plan as it was stored, not one rebuilt from the
        // stops. Rebuilding it would go through the *current* profile and
        // travel mode, and a plan that differs by a byte is a different
        // `planRef`, a different day key, and a refusal.
        planBytes: dispatcher.threads.base64UrlToBytes(route.planB64),
        serviceDay,
        notBefore,
        notAfter,
        // The ticket's own expiry is the term. It is what the 78 bytes
        // are cut to, and cutting them to tonight would hand every
        // parent a link that dies at midnight.
        termNotAfter: route.termToUnixSeconds,
        recipients: [
          ...(device ? [device.publicKeyHex] : []),
          state.consoleDevice.publicKeyHex
        ],
        mode: route.mode,
        // Same rule as Compose: an enrolled seed wins, the configured one
        // is the fallback, null only when there is nothing to dial.
        bootstrap: state.seed?.addresses ?? (config.meshBootstrap.length ? config.meshBootstrap : null)
      });

      // The link is read back off the ticket rather than taken on trust,
      // and it has to be the one the parents were given in September. It
      // can only differ if the console's routing map changed epoch since
      // the route was made (§20.6) — rare, invisible, and the exact
      // failure the whole of §21 exists to prevent, so it stops here.
      if (issued.trackUrl !== route.parentUrl) {
        throw new Error(
          "This day's ticket derives a different link from the one this "
          + `${vocabulary.recipient}s already hold, so it is not being sent. The routing map this `
          + "console is holding has changed since the term was set up; the route has to be remade "
          + "on the new map, and the link reissued."
        );
      }

      const day: StoredRouteDay = {
        serviceDay,
        notBefore: issued.notBefore,
        notAfter: issued.notAfter,
        deviceId: device?.id ?? null,
        sealedB64: issued.sealedB64,
        driverUrl: issued.driverUrl,
        mintedUnixSeconds: nowSeconds()
      };

      onSave({ ...route, days: [day, ...route.days.filter(entry => entry.serviceDay !== serviceDay)] });
      onDispatchDay(route, day, {
        id: localId("job"),
        createdUnixSeconds: nowSeconds(),
        profile: state.profile ?? "courier",
        stops: route.stops,
        planKey: route.planKey,
        planRefHex: route.planRefHex,
        jobIdHex: issued.jobIdHex,
        deviceId: device?.id ?? null,
        driverName: device?.name ?? null,
        driverless: !device,
        heldBy: device?.id ?? null,
        mode: route.mode,
        travelMode: route.travelMode,
        // The *day's* window, which is what actually bounds the run. The
        // ticket's own expiry is the term, and it is not this number.
        notAfter: issued.notAfter,
        // The term's link, never the day's — and it came off the ticket,
        // checked against the parents' link above.
        trackUrl: issued.trackUrl,
        driverUrl: issued.driverUrl,
        sealedB64: issued.sealedB64,
        optimized: false,
        totalMeters: null,
        startedUnixSeconds: null,
        finishedUnixSeconds: null,
        usedRungs: [1, 3],
        routeId: route.id,
        serviceDay,
        offerId: null
      });
    } catch (error) {
      setProblem(error instanceof Error ? error.message : String(error));
    } finally {
      setBusy(false);
    }
  }, [dispatcher, route, state, devices, deviceId, civil, serviceDay, vocabulary, onSave, onDispatchDay]);

  return (
    <Panel eyebrow={route.name}>
      <p className="subtitle">
        {plural(route.stops.length, vocabulary.stop)} · term {dateWords(route.termFromUnixSeconds)} to{" "}
        {dateWords(route.termToUnixSeconds)}
      </p>

      <div style={{ marginTop: "0.7rem" }}>
        <Copyable
          label={`The link ${vocabulary.recipient}s keep`}
          value={route.parentUrl}
          href={route.parentUrl}
          hint={
            "Send this once, in September. It does not move when the driver changes, when a phone "
            + "is lost, or when the spare bus goes out — it is made from the route’s own identity, "
            + "and the authority to move the vehicle is a separate, short-lived thing."
          }
        />
      </div>

      <hr className="divider" />

      <p className="field__label">Today — service day {serviceDay}</p>
      {already ? (
        <div className="stack stack--tight" style={{ marginTop: "0.4rem" }}>
          <Note title="Today’s authority is minted." tone="settled">
            Signed for {Math.round((already.notAfter - already.notBefore) / 3600)} hours and no
            longer. If that phone is lost this evening, the seed on it is worthless by tomorrow
            night at the latest — and no parent’s link has to change.
          </Note>
          <div className="handover handover--wide">
            <div>
              <Qr value={already.driverUrl} alt="Today’s run, as a code for a phone camera" />
              <p className="field__hint" style={{ marginTop: "0.45rem" }}>
                Point today’s phone at this. What the camera reads is ciphertext.
              </p>
            </div>
            <div className="stack stack--tight">
              <Copyable
                label="Today’s run, sealed"
                value={already.driverUrl}
                hint={
                  "One artifact, not two. The certificate that says this key speaks for the "
                  + `${vocabulary.route} today is inside these bytes and signed with them, so there `
                  + "is nothing to send alongside it and nothing to send in the wrong order."
                }
              />
              <div className="row">
                <button
                  type="button"
                  className="button"
                  onClick={() => downloadFile(
                    `${route.name.replace(/\W+/gu, "-").toLowerCase()}-${already.serviceDay}.wayfindjob`,
                    already.driverUrl,
                    "text/plain"
                  )}
                >
                  Download the file
                </button>
                <span className="small muted">
                  The identical sealed bytes, for a round too big to scan.
                </span>
              </div>
              <DayReadBack
                day={already}
                route={route}
                dispatcher={dispatcher}
                consolePrivateKeyHex={state.consoleDevice.privateKeyHex}
                vocabulary={vocabulary}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="stack stack--tight" style={{ marginTop: "0.4rem" }}>
          <Field label={`Who is driving today`}>
            <select className="select" value={deviceId} onChange={event => setDeviceId(event.target.value)}>
              <option value="">Nobody yet</option>
              {devices.map(device => (
                <option key={device.id} value={device.id}>{device.name}</option>
              ))}
            </select>
          </Field>
          {problem ? (
            <div className="banner banner--halt" role="alert">
              <span className="banner__mark">No</span>
              <p className="banner__text">{problem}</p>
            </div>
          ) : null}
          <div className="row row--end">
            <button
              type="button"
              className="button button--primary"
              disabled={!dispatcher || busy}
              onClick={() => { void dispatchToday(); }}
            >
              {busy ? "Minting…" : "Send today’s run"}
            </button>
          </div>
        </div>
      )}

      {route.days.length > 1 ? (
        <>
          <hr className="divider" />
          <p className="small muted">
            Days minted: {route.days.map(day => day.serviceDay).join(", ")}. Every one of them is
            derived from the same root and hands out the same link above — that is what makes them
            days of one {vocabulary.route} rather than {route.days.length} separate rounds. None
            needs keeping: the root re-derives any of them.
          </p>
        </>
      ) : null}

      <hr className="divider" />
      <Note title="The root never leaves this console." tone="quiet">
        A driver’s phone is a device that gets lost, sold, and handed to a repair shop. What goes on
        it is one day of authority. What is here is the route itself — and a browser is not a
        keystore, which is worth knowing before a term’s worth of routes lives in one.
      </Note>
    </Panel>
  );
}

/**
 * What is actually on the phone, read back out of the bytes.
 *
 * Not a restatement of what this console remembers doing. It opens the
 * sealed artifact with the console's own device key and asks
 * `classifyThreadArtifact` what it is, then derives the follow link from
 * the ticket itself and compares it with the one the recipients hold.
 * Both answers come from the artifact, which is the only place worth
 * asking: the failure this replaced was invisible precisely because
 * every value the app held about it looked right.
 */
function DayReadBack({
  day,
  route,
  dispatcher,
  consolePrivateKeyHex,
  vocabulary
}: {
  day: StoredRouteDay;
  route: StoredRoute;
  dispatcher: Dispatcher | null;
  consolePrivateKeyHex: string;
  vocabulary: Vocabulary;
}): JSX.Element | null {
  const [read, setRead] = useState<ReadTicket | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (!dispatcher || !consolePrivateKeyHex) return;
    let live = true;
    setRead(null);
    setProblem(null);
    dispatcher.readTicket(day.sealedB64, consolePrivateKeyHex).then(
      result => { if (live) setRead(result); },
      (error: unknown) => {
        if (live) setProblem(error instanceof Error ? error.message : String(error));
      }
    );
    return () => { live = false; };
  }, [dispatcher, consolePrivateKeyHex, day.sealedB64]);

  if (problem) {
    return (
      <Note title="This console cannot open what it sent." tone="halt">
        {problem}
      </Note>
    );
  }
  if (!read) return null;

  const sameLink = read.followUrl === route.parentUrl;
  return (
    <Note
      title={read.routeDay
        ? `These bytes say: ${vocabulary.route} day ${read.serviceDay}.`
        : "These bytes are an ordinary job, not a route day."}
      tone={read.routeDay && sameLink ? "settled" : "halt"}
    >
      {read.routeDay
        ? <>
          Opened and read back here, not remembered: a ticket carrying this {vocabulary.route}’s
          day certificate, {plural(read.stopCount, vocabulary.stop)}, and a follow link that
          {sameLink ? " is byte for byte the one " : " is not the one "}
          {vocabulary.recipient}s already hold. It will be the same link again tomorrow, because
          it is derived from the {vocabulary.route}’s root and the term — not from the key that
          expires tonight.
        </>
        : <>
          It would publish under its own key, on a thread nobody is subscribed to. Do not send
          this.
        </>}
    </Note>
  );
}

function isoDate(unixSeconds: number): string {
  const date = new Date(unixSeconds * 1000);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
